import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
  Box,
} from '@mui/material';
import parameterService from '../../services/parameterService';

const ParameterForm = ({ open, onClose, parameter, onSuccess }) => {
  const [formData, setFormData] = useState({
    param_key: '',
    title: '',
    description: '',
    param_value: '',
    value_type: 'string',
    category: 'general',
    is_active: true,
    is_editable: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (parameter) {
      // Modo edición
      setFormData({
        param_key: parameter.param_key || '',
        title: parameter.title || '',
        description: parameter.description || '',
        param_value: parameter.param_value || '',
        value_type: parameter.value_type || 'string',
        category: parameter.category || 'general',
        is_active: parameter.is_active !== undefined ? parameter.is_active : true,
        is_editable: parameter.is_editable !== undefined ? parameter.is_editable : true,
      });
    } else {
      // Modo creación
      setFormData({
        param_key: '',
        title: '',
        description: '',
        param_value: '',
        value_type: 'string',
        category: 'general',
        is_active: true,
        is_editable: true,
      });
    }
    setError('');
  }, [parameter, open]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validaciones
      if (!formData.param_key || !formData.title || !formData.param_value) {
        setError('Key, título y valor son obligatorios');
        setLoading(false);
        return;
      }

      // Validar formato de key (solo letras, números y guion bajo)
      if (!/^[a-z0-9_]+$/.test(formData.param_key)) {
        setError('El key solo puede contener letras minúsculas, números y guion bajo');
        setLoading(false);
        return;
      }

      // Validar valor según tipo
      if (formData.value_type === 'number' && isNaN(formData.param_value)) {
        setError('El valor debe ser un número');
        setLoading(false);
        return;
      }

      if (formData.value_type === 'boolean' && !['true', 'false', '1', '0'].includes(formData.param_value.toLowerCase())) {
        setError('El valor debe ser true o false');
        setLoading(false);
        return;
      }

      if (formData.value_type === 'json') {
        try {
          JSON.parse(formData.param_value);
        } catch (e) {
          setError('El valor debe ser un JSON válido');
          setLoading(false);
          return;
        }
      }

      // Preparar datos
      const parameterData = {
        param_key: formData.param_key.toLowerCase(),
        title: formData.title,
        description: formData.description || null,
        param_value: formData.param_value,
        value_type: formData.value_type,
        category: formData.category,
        is_active: formData.is_active,
        is_editable: formData.is_editable,
      };

      if (parameter) {
        // Actualizar
        await parameterService.update(parameter.id, parameterData);
      } else {
        // Crear
        await parameterService.create(parameterData);
      }

      onSuccess();
      onClose();
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error al guardar parámetro';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const valueTypes = [
    { value: 'string', label: 'Texto' },
    { value: 'number', label: 'Número' },
    { value: 'boolean', label: 'Booleano (true/false)' },
    { value: 'json', label: 'JSON' },
  ];

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'security', label: 'Seguridad' },
    { value: 'payments', label: 'Pagos' },
    { value: 'files', label: 'Archivos' },
    { value: 'email', label: 'Email' },
    { value: 'notifications', label: 'Notificaciones' },
    { value: 'system', label: 'Sistema' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {parameter ? 'Editar Parámetro' : 'Nuevo Parámetro'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Key (Identificador)"
                name="param_key"
                value={formData.param_key}
                onChange={handleChange}
                required
                disabled={loading || !!parameter} // No permitir editar key
                helperText="Solo minúsculas, números y guion bajo. Ej: session_timeout"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Categoría"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                disabled={loading}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Título"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Descripción"
                name="description"
                value={formData.description}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="Tipo de Valor"
                name="value_type"
                value={formData.value_type}
                onChange={handleChange}
                required
                disabled={loading}
              >
                {valueTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Valor"
                name="param_value"
                value={formData.param_value}
                onChange={handleChange}
                required
                disabled={loading || (parameter && !parameter.is_editable)}
                multiline={formData.value_type === 'json'}
                rows={formData.value_type === 'json' ? 3 : 1}
                helperText={
                  formData.value_type === 'number' ? 'Ingrese un número' :
                  formData.value_type === 'boolean' ? 'true o false' :
                  formData.value_type === 'json' ? 'JSON válido' :
                  'Texto'
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={handleChange}
                      name="is_active"
                      disabled={loading}
                    />
                  }
                  label="Activo"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_editable}
                      onChange={handleChange}
                      name="is_editable"
                      disabled={loading}
                    />
                  }
                  label="Editable"
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Los parámetros no editables solo pueden ser activados/desactivados
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={loading} size="large">
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            size="large"
          >
            {loading ? <CircularProgress size={24} /> : (parameter ? 'Actualizar' : 'Crear')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ParameterForm;
